import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability'
import { intersection } from 'ramda'

import { UserRole } from './types'

export type AdviserUserType = 'firm' | 'corporate'

export type Actions =
  | 'read'
  | 'create'
  | 'update'
  | 'impersonate'
  | 'invite'
  | 'block'
  | 'filter'
export type Subjects =
  | 'user'
  | 'firm'
  | 'employee'
  | 'employee-adviser'
  | 'employee-access'
  | 'user-settings'
  | 'client-adviser'

export type AppAbility = MongoAbility<[Actions, Subjects]>

export const defineAbilityFor = (roles?: UserRole[]): AppAbility => {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(
    createMongoAbility,
  )

  if (roles?.includes(UserRole.FirmUser)) {
    can('read', 'client-adviser')
    can('read', 'employee-adviser')
    can('update', 'user-settings')
    can('filter', 'employee')
    cannot('block', 'employee')
    can('update', 'employee-access')
  }

  if (roles?.includes(UserRole.MasterFirmUser)) {
    can('read', 'client-adviser') // can see if an employee is an adviser
    can('read', 'employee-adviser') // can see if an employee is an adviser
    can('read', 'firm')
    can('read', 'employee')
    can('create', 'employee')
    can('update', 'employee')
    can('invite', 'employee')
    can('block', 'employee')
    can('update', 'user-settings')
    can('filter', 'employee')
    can('update', 'employee-access')
  }

  if (roles?.includes(UserRole.CorporateUser)) {
    can('update', 'user-settings')
    cannot('block', 'employee')
    cannot('filter', 'employee')
    cannot('update', 'employee-access')
  }

  if (roles?.includes(UserRole.MasterCorporateUser)) {
    can('read', 'firm')
    can('read', 'employee')
    can('create', 'employee')
    can('update', 'employee')
    can('invite', 'employee')
    can('block', 'employee')
    can('update', 'user-settings')
    cannot('filter', 'employee')
    cannot('update', 'employee-access')
  }

  if (roles?.includes(UserRole.ImpersonatedUser)) {
    cannot('create', 'employee')
    cannot('update', 'employee')
    cannot('update', 'employee-access')
    cannot('block', 'employee')
    cannot('invite', 'employee')
    cannot('update', 'user-settings')
    can('impersonate', 'user')
  }

  return build()
}

const adviserAppRoles = [
  UserRole.FirmUser,
  UserRole.MasterFirmUser,
  UserRole.CorporateUser,
  UserRole.MasterCorporateUser,
]

const corporateAdviserRoles = [
  UserRole.CorporateUser,
  UserRole.MasterCorporateUser,
]

const firmAdviserRoles = [UserRole.FirmUser, UserRole.MasterFirmUser]

export function isAdviserAppRole(roles?: UserRole[]) {
  if (!roles) return false
  return intersection(adviserAppRoles, roles).length > 0
}

export function getAdviserUserType(
  roles?: UserRole[],
): AdviserUserType | undefined {
  if (!roles) return undefined
  if (intersection(corporateAdviserRoles, roles).length > 0) return 'corporate'
  if (intersection(firmAdviserRoles, roles).length > 0) return 'firm'
  return undefined
}
